import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import ModuleList from './ModuleList';
import ClassroomsList from './ClassroomsList';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserTimezone } from '../utils/timezoneUtils';
import { useData } from '../contexts/DataContext';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';

const Dashboard = ({ deleteModule, userPlan, setUserPlan }) => {
  const { t } = useLanguage();
  const { modules, loading, error, fetchModulesForDashboard, refreshModules } = useData();
  const [isEditMode, setIsEditMode] = useState(false);

  // Check if modules are loading
  const modulesLoading = loading.modules;

  // Debug: Log modules and loading state


  // Wrapper function to delete module and refresh modules
  const handleDeleteModule = async (moduleId) => {
    const result = await deleteModule(moduleId);
    if (!result) {
      // If deletion was successful, refresh modules
      refreshModules();
    }
    return result;
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const userTimezone = getUserTimezone();
    return date.toLocaleDateString(undefined, {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleManageSubscription = async () => {
    try {
      const { data: { session: sessionObj } } = await supabase.auth.getSession();
      const accessToken = sessionObj?.access_token;

      if (!accessToken) {
        alert(t('storeMustBeLoggedIn'));
        return;
      }

      const res = await fetch(`${backendUrl}/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        throw new Error('Invalid server response');
      }

      if (!res.ok) {
        if (res.status === 404 || data.error === 'NO_SUBSCRIPTIONS') {
          alert(t('noSubscriptionsToManage') || 'You have not yet purchased anything and have no subscriptions to manage.');
        } else {
          alert(t('failedToManageSubscription') + ' ' + (data.error || data.message || 'Unknown error'));
        }
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(t('failedToManageSubscription') + ' ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error managing subscription:', err);
      alert(t('failedToManageSubscription') + ' ' + err.message);
    }
  };

  if (modulesLoading) {
    return (
      <div className="dashboard-container" style={{ paddingTop: '20px' }} id="main-content">
        <div className="dashboard-content" style={{ textAlign: 'center', padding: '2rem' }}>
          {t('loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container" style={{ paddingTop: '20px' }} id="main-content">
        <div className="dashboard-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>
          <button onClick={fetchModulesForDashboard} className="btn btn-primary">
            {t('retry') || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ paddingTop: '20px' }} id="main-content">
      <div className="dashboard-content">
        {/* Plan Information */}
        {userPlan && (
          <div className="plan-info" style={{
            background: 'linear-gradient(135deg, #6a994e 0%, #386641 100%)',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(56, 102, 65, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              {t('currentPlan')}: <strong>{userPlan.plan === 'pro' ? t('planPro') : t('planFree')}</strong>
            </div>
            {userPlan.plan === 'pro' && userPlan.premiumExpiresAt && (
              <>
                <div style={{ fontSize: '0.8rem', opacity: 0.95, marginBottom: '0.8rem' }}>
                  {t('expiresOn')} {formatExpiryDate(userPlan.premiumExpiresAt)}
                </div>
                <button
                  onClick={handleManageSubscription}
                  style={{
                    display: 'inline-block',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {t('manageSubscription')}
                </button>
              </>
            )}
            {userPlan.plan === 'free' && (
              <>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.8rem', marginBottom: '0.5rem' }}>
                  {t('wantToLearnMore')}
                </div>
                <Link
                  to="/store"
                  style={{
                    display: 'inline-block',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {t('upgradeToPremium')}
                </Link>
              </>
            )}
          </div>
        )}

        {/* Classrooms Section */}
        <div style={{ marginTop: '2rem' }}>
          <ClassroomsList />
        </div>

        {/* My Modules Section */}
        <div className="modules-section">
          <div className="modules-section-header">
            <h2>{t('myModules') || 'My Modules'}</h2>
          </div>
          
          <p className="dashboard-welcome" style={{ textAlign: 'center', width: '100%', marginBottom: '1rem' }}>{t('welcome')}</p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            <Link to="/create-module" className="btn btn-primary create-module-button" style={{ minWidth: '180px' }}>+ {t('createNewModule')}</Link>
            {Array.isArray(modules) && modules.length > 0 && (
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className={`btn ${isEditMode ? 'btn-secondary' : 'btn-primary'} create-module-button`}
                style={{
                  minWidth: '180px',
                  width: 'auto',
                  boxSizing: 'border-box',
                  textAlign: 'center'
                }}
              >
                {isEditMode ? t('cancel') : t('editModules')}
              </button>
            )}
          </div>

          {/* Wrap the module list with a flex container to center it */}
          <div className="module-cards-container">
            <ModuleList
              modules={Array.isArray(modules) ? [...modules].filter(m => !m.classroom_id).sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) : []}
              deleteModule={handleDeleteModule}
              isEditMode={isEditMode}
              onExitEditMode={() => setIsEditMode(false)}
              onSaveSuccess={() => {
                setIsEditMode(false);
                refreshModules();
              }}
            />
          </div>
        </div>


      </div>
    </div>
  );
};

export default Dashboard;